import * as React from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Divider from '@mui/joy/Divider'
import Checkbox from '@mui/joy/Checkbox'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Tabs from '@mui/joy/Tabs'
import TabList from '@mui/joy/TabList'
import Tab from '@mui/joy/Tab'
import TabPanel from '@mui/joy/TabPanel'
import Typography from '@mui/joy/Typography'
import Alert from '@mui/joy/Alert'
import { CircularProgress } from '@mui/joy'
import { Link } from 'react-router-dom'
import { DocumentDrainResult, DocumentSource } from '@numeo/types-core'
import { SelectedDocument, apiErrorMessage, documentProcessingTestApi } from './api'
import PipelineJobsPicker from './components/PipelineJobsPicker'
import ExistingDocumentsPicker from './components/ExistingDocumentsPicker'
import DocumentUploader from './components/DocumentUploader'

interface TriggerResult {
    mode: 'immediate' | 'batch'
    result: Record<string, unknown>
}

/**
 * Manual test surface for the document-processing pipeline (ADR-022) — same
 * entry points production uses (no test-only shortcuts): a form submits a doc
 * reference through the real immediate/batch routing decision, and a button
 * starts the cloud drain (ADR-022.1) now instead of waiting for the hourly tick.
 *
 * The document reference is always real — picked from documents already run
 * through this pipeline, picked from the production ratecon corpus, or
 * uploaded fresh — never hand-typed.
 */
export default function DocumentProcessingTestPage() {
    const [selectedDoc, setSelectedDoc] = React.useState<SelectedDocument | null>(null)
    const [projectName, setProjectName] = React.useState('')
    const [source, setSource] = React.useState<DocumentSource>(DocumentSource.UPLOAD)
    const [docTypeHint, setDocTypeHint] = React.useState('')
    const [skipOcr, setSkipOcr] = React.useState(false)

    const [triggering, setTriggering] = React.useState(false)
    const [triggerResult, setTriggerResult] = React.useState<TriggerResult | null>(null)
    const [triggerError, setTriggerError] = React.useState<string | null>(null)

    const [draining, setDraining] = React.useState(false)
    const [drainResult, setDrainResult] = React.useState<DocumentDrainResult | null>(null)
    const [drainError, setDrainError] = React.useState<string | null>(null)

    const canTrigger = !!selectedDoc && projectName.trim() && !triggering

    const handleSelect = (doc: SelectedDocument) => {
        setSelectedDoc(doc)
        if (doc.projectName && !projectName.trim()) setProjectName(doc.projectName)
    }

    const handleTrigger = async () => {
        if (!selectedDoc) return
        setTriggering(true)
        setTriggerError(null)
        setTriggerResult(null)
        try {
            const result = await documentProcessingTestApi.trigger({
                projectName: projectName.trim(),
                blobUri: selectedDoc.blobUri,
                source,
                docTypeHint: docTypeHint.trim() || undefined,
                skipOcr: skipOcr || undefined,
            })
            setTriggerResult(result)
        } catch (err) {
            const message = apiErrorMessage(err, 'Failed to trigger the test document')
            setTriggerError(message)
        } finally {
            setTriggering(false)
        }
    }

    const handleDrain = async () => {
        setDraining(true)
        setDrainError(null)
        setDrainResult(null)
        try {
            setDrainResult(await documentProcessingTestApi.drain())
        } catch (err) {
            const message = apiErrorMessage(err, 'Starting the drain failed')
            setDrainError(message)
        } finally {
            setDraining(false)
        }
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2, maxWidth: 900 }}>
            <Typography level="h2" component="h1">
                Document Processing Test
            </Typography>
            <Typography level="body-sm" color="neutral">
                Submits through the real entry points — `source` decides immediate vs. batch mode exactly like production. Batch-mode submissions land in{' '}
                <Link to="/document-pipeline/document-jobs">Document Jobs</Link> as <code>ocr_queued</code>; the cloud drain picks them up on its hourly tick,
                or start it below.
            </Typography>

            <Card variant="outlined" sx={{ p: 3 }}>
                <Typography level="title-md" sx={{ mb: 2 }}>
                    Submit a Test Document
                </Typography>

                {selectedDoc ? (
                    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                                <Typography level="body-sm" fontWeight="bold">
                                    Selected Document{selectedDoc.filename ? `: ${selectedDoc.filename}` : ''}
                                </Typography>
                                <Typography level="body-xs" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                                    contentHash: {selectedDoc.contentHash}
                                </Typography>
                                <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                    blobUri: {selectedDoc.blobUri}
                                </Typography>
                            </Box>
                            <Button size="sm" variant="plain" onClick={() => setSelectedDoc(null)}>
                                Change document
                            </Button>
                        </Box>
                    </Sheet>
                ) : (
                    <Tabs defaultValue={0} sx={{ mb: 2 }}>
                        <TabList>
                            <Tab>My Test Jobs</Tab>
                            <Tab>Existing Documents</Tab>
                            <Tab>Upload New</Tab>
                        </TabList>
                        <TabPanel value={0} sx={{ p: 2 }}>
                            <PipelineJobsPicker onSelect={handleSelect} />
                        </TabPanel>
                        <TabPanel value={1} sx={{ p: 2 }}>
                            <ExistingDocumentsPicker onSelect={handleSelect} />
                        </TabPanel>
                        <TabPanel value={2} sx={{ p: 2 }}>
                            <DocumentUploader onSelect={handleSelect} />
                        </TabPanel>
                    </Tabs>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl size="sm">
                        <FormLabel>Project Name</FormLabel>
                        <Input size="sm" value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="the tenant's project name" />
                    </FormControl>
                    <FormControl size="sm">
                        <FormLabel>Source (decides immediate vs. batch mode)</FormLabel>
                        <Select size="sm" value={source} onChange={(_, val) => val && setSource(val as DocumentSource)}>
                            {Object.values(DocumentSource).map((s) => (
                                <Option key={s} value={s}>
                                    {s} {s === DocumentSource.EMAIL ? '(batch)' : '(immediate)'}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="sm">
                        <FormLabel>Doc Type Hint (optional)</FormLabel>
                        <Input size="sm" value={docTypeHint} onChange={(e) => setDocTypeHint(e.target.value)} placeholder="e.g. ratecon" />
                    </FormControl>
                    <Checkbox
                        size="sm"
                        checked={skipOcr}
                        onChange={(e) => setSkipOcr(e.target.checked)}
                        label="Skip OCR — go straight to visual/LLM extraction"
                    />
                    <Box>
                        <Button size="sm" variant="solid" color="primary" disabled={!canTrigger} onClick={handleTrigger}>
                            {triggering ? <CircularProgress size="sm" /> : 'Submit Test Document'}
                        </Button>
                    </Box>

                    {triggerError && (
                        <Alert color="danger" variant="soft">
                            {triggerError}
                        </Alert>
                    )}
                    {triggerResult && (
                        <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
                            <Typography level="body-sm" fontWeight="bold">
                                Mode: {triggerResult.mode}
                            </Typography>
                            <Typography level="body-xs" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', mt: 0.5 }}>
                                {JSON.stringify(triggerResult.result, null, 2)}
                            </Typography>
                        </Sheet>
                    )}
                </Box>
            </Card>

            <Divider />

            <Card variant="outlined" sx={{ p: 3 }}>
                <Typography level="title-md" sx={{ mb: 1 }}>
                    Cloud Drain
                </Typography>
                <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                    Starts the <code>document-worker</code> Cloud Run Job if work is due: its tasks run OCR, then task 0 applies finished batches and submits
                    the next one. It also runs every hour. Batch results arrive on a later drain, once Vertex finishes.
                </Typography>
                <Box>
                    <Button size="sm" variant="outlined" disabled={draining} onClick={handleDrain} sx={{ mb: 1 }}>
                        {draining ? <CircularProgress size="sm" /> : 'Start Drain'}
                    </Button>
                    {drainError && (
                        <Alert color="danger" variant="soft">
                            {drainError}
                        </Alert>
                    )}
                    {drainResult && (
                        <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
                            <Typography level="body-xs" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                {drainResult.started ? `Started ${drainResult.taskCount} task(s)` : `Not started: ${drainResult.reason}`}
                            </Typography>
                        </Sheet>
                    )}
                </Box>
            </Card>
        </Box>
    )
}
